import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useContext } from "react";
import {
  getAppointmentTypes,
  getAppointmentType,
  getAppointmentPackages,
  getAppointmentCredits,
  getAppointmentDiscounts,
  GetAppointmentTypeStaffResult,
  getAppointmentTypeStaff,
  GetAppointmentDiscounts,
  GetAppointmentTypesResult,
  GetAppointmentPackages,
  GetAppointmentCreditsResult,
  getAppointmentDiscount,
  GetAppointmentDiscount,
  GetAppointmentTypeResult,
  getAppointmentPackage,
  GetAppointmentPackageResult,
  GetAppointmentHistoryResult,
  getAppointmentHistory,
  getAppointment,
  GetAppointmentResult,
  postAppointmentListView,
  AppointmentEvent,
} from "./appointmentApi";
import { AppContext } from "../context/app";

export function useAppointmentTypeQuery(
  appointmentUuid: string,
  options?: UseQueryOptions<GetAppointmentTypeResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentTypeResult>({
    ...options,
    queryKey: ["appointment-types", app.client!.uuid, appointmentUuid],
    queryFn: () => getAppointmentType(app.client!.uuid, appointmentUuid),
  });
}

export function useAppointmentTypesQuery(
  options?: UseQueryOptions<GetAppointmentTypesResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentTypesResult>({
    ...options,
    queryKey: ["appointment-types", app.client!.uuid],
    queryFn: () => getAppointmentTypes(app.client!.uuid),
  });
}

export function useAppointmentPackageQuery(
  appointmentPackageUuid: string,
  options?: UseQueryOptions<GetAppointmentPackageResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentPackageResult>({
    ...options,
    queryKey: [
      "appointment-packages",
      app.client!.uuid,
      appointmentPackageUuid,
    ],
    queryFn: () =>
      getAppointmentPackage(app.client!.uuid, appointmentPackageUuid),
  });
}

export function useAppointmentPackagesQuery(
  options?: UseQueryOptions<GetAppointmentPackages>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentPackages>({
    ...options,
    queryKey: ["appointment-packages", app.client!.uuid],
    queryFn: () => getAppointmentPackages(app.client!.uuid),
  });
}

export function useAppointmentCreditsQuery(
  userUuid: string,
  appointmentTypeUuid: string,
  options?: UseQueryOptions<GetAppointmentCreditsResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentCreditsResult>({
    ...options,
    queryKey: [
      "appointment-credits",
      app.client!.uuid,
      userUuid,
      appointmentTypeUuid,
    ],
    queryFn: () =>
      getAppointmentCredits(app.client!.uuid, userUuid, appointmentTypeUuid),
    enabled: !!userUuid && !!appointmentTypeUuid,
  });
}

export function useAppointmentDiscountsQuery(
  options?: UseQueryOptions<GetAppointmentDiscounts>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentDiscounts>({
    ...options,
    queryKey: ["appointment-discounts", app.client!.uuid],
    queryFn: () => getAppointmentDiscounts(app.client!.uuid),
  });
}

export function useAppointmentDiscountQuery(
  appointmentDiscountUuid: string,
  options?: UseQueryOptions<GetAppointmentDiscount>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentDiscount>({
    ...options,
    queryKey: [
      "appointment-discount",
      app.client!.uuid,
      appointmentDiscountUuid,
    ],
    queryFn: () =>
      getAppointmentDiscount(app.client!.uuid, appointmentDiscountUuid),
  });
}

export function useAppointmentTypeStaffQuery(
  appointmentTypeUuid: string,
  options?: UseQueryOptions<GetAppointmentTypeStaffResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentTypeStaffResult>({
    ...options,
    queryKey: ["appointment-type-staff", app.client!.uuid, appointmentTypeUuid],
    queryFn: () =>
      getAppointmentTypeStaff(app.client!.uuid, appointmentTypeUuid),
  });
}

export function useAppointmentHistoryQuery(
  memberId: string,
  options?: UseQueryOptions<GetAppointmentHistoryResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentHistoryResult>({
    ...options,
    queryKey: ["appointment-history", app.client!.uuid, memberId],
    queryFn: () => getAppointmentHistory(app.client!.uuid, memberId),
  });
}

export function useAppointmentQuery(
  appointmentUuid: string,
  options?: UseQueryOptions<GetAppointmentResult>,
) {
  const app = useContext(AppContext);
  return useQuery<GetAppointmentResult>({
    ...options,
    queryKey: ["appointment-schedule", app.client!.uuid, appointmentUuid],
    queryFn: () => getAppointment(app.client!.uuid, appointmentUuid),
  });
}

export function useAppointmentScheduleQuery(
  {
    from,
    to,
  }: {
    from: string;
    to: string;
  },
  options?: UseQueryOptions<AppointmentEvent[]>,
) {
  const { client } = useContext(AppContext);
  return useQuery<AppointmentEvent[]>({
    ...options,
    queryKey: ["appointment-schedule", from, to],
    queryFn: () =>
      postAppointmentListView(client!.uuid, client.timezone, from, to),
  });
}
